export const SHARED_CHART_ACCESS_LEVELS = ['Read', 'Contribute', 'Chart', 'Transfer'];

export const SHARED_CHART_PERMISSION_MESSAGE = 'Your shared access is read-only or has expired.';

const ACCESS_RANK = SHARED_CHART_ACCESS_LEVELS.reduce((acc, level, index) => {
  acc[level.toLowerCase()] = index;
  return acc;
}, {});

export function normalizeSharedChartAccessLevel(accessLevel) {
  if (accessLevel == null) return null;
  const key = String(accessLevel).trim().toLowerCase();
  const canonical = SHARED_CHART_ACCESS_LEVELS.find(level => level.toLowerCase() === key);
  return canonical || null;
}

export function getSharedChartAccessRank(accessLevel) {
  const normalized = normalizeSharedChartAccessLevel(accessLevel);
  return normalized ? ACCESS_RANK[normalized.toLowerCase()] : -1;
}

export function canReadSharedChart(accessLevel) {
  return getSharedChartAccessRank(accessLevel) >= ACCESS_RANK.read;
}

export function canChartSharedChart(accessLevel) {
  return getSharedChartAccessRank(accessLevel) >= ACCESS_RANK.chart;
}

export function getHighestAccessLevel(items = []) {
  const values = Array.isArray(items) ? items : [items];
  return values.reduce((highest, item) => {
    const level = normalizeSharedChartAccessLevel(
      typeof item === 'string' ? item : item?.accessLevel ?? item?.AccessLevel
    );
    return getSharedChartAccessRank(level) > getSharedChartAccessRank(highest) ? level : highest;
  }, null);
}

export function isSharedGrantActive(item = {}) {
  const revoked = item.revokedAt || item.RevokedAt || item.revoked || item.isRevoked || item.IsRevoked;
  if (revoked) return false;

  const status = String(item.status ?? item.Status ?? '').trim().toLowerCase();
  if (status === 'revoked' || status === 'expired') return false;

  const expiresAt = item.expiresAt ?? item.ExpiresAt;
  if (expiresAt) {
    const expiry = new Date(expiresAt);
    if (!Number.isNaN(expiry.getTime()) && expiry <= new Date()) return false;
  }

  return true;
}

export function getSharedChartAccessLabel(accessLevel) {
  const normalized = normalizeSharedChartAccessLevel(accessLevel);
  if (normalized === 'Read') return 'Read only';
  if (normalized === 'Contribute') return 'Can contribute';
  if (normalized === 'Chart') return 'Can chart';
  if (normalized === 'Transfer') return 'Transfer access';
  return 'Unknown access';
}

function getSharedPregnancyGroupKey(item = {}, index) {
  const pregnancyRef = item.pregnancyReferenceId ?? item.PregnancyReferenceId ?? item.resourceReferenceId ?? item.ResourceReferenceId ?? item.referenceId ?? item.ReferenceId;
  const clientRef = item.clientReferenceId ?? item.ClientReferenceId ?? item.clientId ?? item.ClientId;
  if (pregnancyRef || clientRef) return `${pregnancyRef || 'no-pregnancy'}:${clientRef || 'no-client'}`;
  return `row-${index}`;
}

function getGrantReferenceId(item = {}) {
  return item.crossOrgAccessGrantReferenceId ?? item.CrossOrgAccessGrantReferenceId ?? item.grantReferenceId ?? item.GrantReferenceId ?? item.grantId ?? item.GrantId ?? null;
}

export function collapseSharedPregnancyGrants(items = []) {
  const groups = new Map();

  (Array.isArray(items) ? items : []).forEach((item, index) => {
    if (!isSharedGrantActive(item)) return;

    const key = getSharedPregnancyGroupKey(item, index);
    const existing = groups.get(key);
    const itemLevel = normalizeSharedChartAccessLevel(item.accessLevel ?? item.AccessLevel);
    const grantReferenceId = getGrantReferenceId(item);

    if (!existing) {
      groups.set(key, {
        ...item,
        accessLevel: itemLevel,
        crossOrgAccessGrantReferenceId: grantReferenceId,
        crossOrgAccessGrantReferenceIds: grantReferenceId ? [grantReferenceId] : [],
      });
      return;
    }

    const highestLevel = getHighestAccessLevel([existing.accessLevel, itemLevel]);
    const keepItem = getSharedChartAccessRank(itemLevel) > getSharedChartAccessRank(existing.accessLevel);
    const base = keepItem ? { ...existing, ...item } : existing;
    const grantIds = [
      ...(existing.crossOrgAccessGrantReferenceIds || []),
      ...(grantReferenceId ? [grantReferenceId] : []),
    ];

    groups.set(key, {
      ...base,
      accessLevel: highestLevel,
      crossOrgAccessGrantReferenceId: keepItem ? grantReferenceId : existing.crossOrgAccessGrantReferenceId,
      crossOrgAccessGrantReferenceIds: Array.from(new Set(grantIds)),
    });
  });

  return Array.from(groups.values());
}

export function getSharedChartPermissionMessage(err) {
  const raw = String(err?.message ?? err ?? '');
  const status = err?.status ?? err?.Status;
  if (status === 403 || /\b403\b|forbidden|access denied/i.test(raw)) {
    return SHARED_CHART_PERMISSION_MESSAGE;
  }
  return null;
}
